import { apiClient } from '@/lib/api-client';

export interface SecureFileResponse {
  _id: string;
  name: string;
  original_name: string;
  extension: string;
  size: string;
  type: string;
  storage: string;
  created_at: string;
  updated_at: string;
  url: string; // Original file URL
  video_metadata?: {
    codec: string;
    profile?: string | null;
    width: number;
    height: number;
    frame_rate: number;
  } | null;
}

export interface SecureTokenResponse {
  file_id: string;
  token: string;
  // Full URLs (backward-compatible)
  download_url: string;
  view_url: string;
  // New: path-only variants (preferred)
  download_path?: string;
  view_path?: string;
  expires_in: number;
}

export interface SecureFileUploadResponse {
  success: boolean;
  result: SecureFileResponse[];
  message: string;
}

export interface SecureFileInfoResponse {
  success: boolean;
  result: SecureFileResponse;
  message?: string;
}

class SecureFileService {
  private baseUrl = '/secure-file';

  /**
   * Upload files to secure location with authorization token protection
   */
  async uploadFiles(
    files: File[],
    contentType?: string,
  ): Promise<SecureFileUploadResponse> {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });
      if (contentType) {
        formData.append('content_type', contentType);
      }

      const response = await apiClient.post(`${this.baseUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      console.error('Secure file upload failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'File upload failed. Please try again.'
      );
    }
  }

  /**
   * Upload a single file to secure location
   */
  async uploadFile(
    file: File,
    contentType?: string,
  ): Promise<SecureFileUploadResponse> {
    return this.uploadFiles([file], contentType);
  }

  /**
   * Generate secure download token for a file
   */
  async generateDownloadToken(fileId: string): Promise<{ success: boolean; result: SecureTokenResponse }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/token/${fileId}`);
      return response.data;
    } catch (error: any) {
      console.error('Generate download token failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to generate download token.'
      );
    }
  }

  /**
   * Get file information using secure token
   */
  async getFileInfo(token: string): Promise<SecureFileInfoResponse> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/info/${token}`);
      return response.data;
    } catch (error: any) {
      console.error('Get file info failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to get file information.'
      );
    }
  }

  /**
   * Generate secure download URL for a file
   */
  generateDownloadUrl(token: string): string {
    // Prefer backend-generated URLs; this helper is kept for backward compatibility
    // but should be avoided in new code.
    return token;
  }

  /**
   * Generate secure view URL for a file (for images, videos, etc.)
   */
  generateViewUrl(token: string): string {
    // Prefer backend-generated URLs; this helper is kept for backward compatibility
    // but should be avoided in new code.
    return token;
  }

  /**
   * Download file using secure URL (handles authentication automatically)
   */
  async downloadFile(secureUrl: string, filename?: string): Promise<void> {
    try {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = secureUrl;
      link.download = filename || 'download';
      link.target = '_blank';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error('File download failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'File download failed. Please try again.'
      );
    }
  }

  /**
   * Download file using file ID (generates token on-demand)
   */
  async downloadFileById(fileId: string, filename?: string): Promise<void> {
    try {
      // Generate token first
      const tokenResponse = await this.generateDownloadToken(fileId);
      if (!tokenResponse.success) {
        throw new Error('Failed to generate download token');
      }

      const url =
        tokenResponse.result.download_url || tokenResponse.result.download_path;
      if (!url) {
        throw new Error('Download URL is not available from backend.');
      }
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      link.target = '_blank';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error('File download failed:', error);
      throw new Error(
        error.response?.data?.message || 
        'File download failed. Please try again.'
      );
    }
  }

  /**
   * Open file in new tab using secure URL (handles authentication automatically)
   */
  openFileInNewTab(secureUrl: string): void {
    try {
      window.open(secureUrl, '_blank');
    } catch (error: any) {
      console.error('Failed to open file:', error);
      throw new Error('Failed to open file. Please try again.');
    }
  }

  /**
   * Open file in new tab using file ID (generates token on-demand)
   */
  async openFileInNewTabById(fileId: string): Promise<void> {
    try {
      // Generate token first
      const tokenResponse = await this.generateDownloadToken(fileId);
      if (!tokenResponse.success) {
        throw new Error('Failed to generate view token');
      }

      const url =
        tokenResponse.result.view_url || tokenResponse.result.view_path;
      if (!url) {
        throw new Error('View URL is not available from backend.');
      }
      window.open(url, '_blank');
    } catch (error: any) {
      console.error('Failed to open file:', error);
      throw new Error('Failed to open file. Please try again.');
    }
  }

  /**
   * Get file preview URL (for images, videos, PDFs) - generates token on-demand
   */
  async getPreviewUrl(fileId: string): Promise<string> {
    try {
      const tokenResponse = await this.generateDownloadToken(fileId);
      if (!tokenResponse.success) {
        throw new Error('Failed to generate preview token');
      }
      const url =
        tokenResponse.result.view_url || tokenResponse.result.view_path;
      if (!url) {
        throw new Error('Preview URL is not available from backend.');
      }
      return url;
    } catch (error: any) {
      console.error('Failed to generate preview URL:', error);
      throw new Error('Failed to generate preview URL. Please try again.');
    }
  }

  /**
   * Check if file type supports preview
   */
  supportsPreview(extension: string): boolean {
    const previewableExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'ico', 'webp', // Images
      'mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', // Videos
      'pdf', // PDFs
      'txt', 'html', 'css', 'js', 'json', 'xml' // Text files
    ];
    
    return previewableExtensions.includes(extension.toLowerCase());
  }

  /**
   * Get file type icon based on extension
   */
  getFileTypeIcon(extension: string): string {
    const iconMap: { [key: string]: string } = {
      // Images
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'bmp': '🖼️',
      'tiff': '🖼️',
      'ico': '🖼️',
      'webp': '🖼️',
      
      // Videos
      'mp4': '🎥',
      'avi': '🎥',
      'mov': '🎥',
      'wmv': '🎥',
      'flv': '🎥',
      'webm': '🎥',
      
      // Documents
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'txt': '📄',
      'rtf': '📄',
      
      // Presentations
      'ppt': '📊',
      'pptx': '📊',
      
      // Spreadsheets
      'xls': '📈',
      'xlsx': '📈',
      
      // Archives
      'zip': '📦',
      'rar': '📦',
      '7z': '📦',
      'tar': '📦',
      'gz': '📦',
      
      // Code
      'html': '🌐',
      'css': '🎨',
      'js': '⚡',
      'json': '📋',
      'xml': '📋',
      
      // SCORM
      'scorm': '📦',
    };

    return iconMap[extension.toLowerCase()] || '📄';
  }

  /**
   * Format file size in human readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, maxSize: number = 800 * 1024 * 1024): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds maximum allowed size of ${this.formatFileSize(maxSize)}`
      };
    }

    // Check file type (basic validation)
    const allowedTypes = [
      'image/', 'video/', 'audio/', 'application/pdf',
      'application/zip', 'application/x-zip-compressed',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/html', 'text/css', 'application/javascript',
      'application/json', 'application/xml'
    ];

    const isAllowedType = allowedTypes.some(type => file.type.startsWith(type));
    
    if (!isAllowedType) {
      return {
        valid: false,
        error: 'File type not supported'
      };
    }

    return { valid: true };
  }
}

export const secureFileService = new SecureFileService();
